// src/pages/tournament/SetRow.jsx
import React, { useEffect, useRef } from 'react';
import { TrashIcon } from '../../components/icons';
import { formatExact, formatWhen } from '../../utils/time';
import { useNow } from '../../utils/useNow';

const toScore = (raw) => Math.max(0, parseInt(raw, 10) || 0);

// One set: two score inputs saved on Enter or when the field loses focus. The scores are coloured by team
// (the same colours as the team cards), and the leading side is filled in.
// The inputs are uncontrolled (the DOM is the source of truth while typing), so scores that change
// from outside (e.g. the test panel recording one) are pushed into them by the effect below.
function SetRow({ setItem, number, onSave, onDelete, readOnly }) {
    const team1Ref = useRef(null);
    const team2Ref = useRef(null);
    const lastSent = useRef(null);

    // relative times ("5 min ago") only need refreshing for a while after the change
    const recent = Boolean(setItem.updatedAt) && Date.now() - new Date(setItem.updatedAt).getTime() < 90 * 60000;
    const now = useNow(recent);

    const savedTeam1 = setItem.team1Score ?? 0;
    const savedTeam2 = setItem.team2Score ?? 0;

    useEffect(() => {
        // Never overwrite the box being typed in: the answer to an earlier save can arrive late (a slow connection,
        // or the offline engine) and must not undo what has been typed since.
        if (team1Ref.current && document.activeElement !== team1Ref.current) team1Ref.current.value = savedTeam1;
        if (team2Ref.current && document.activeElement !== team2Ref.current) team2Ref.current.value = savedTeam2;
        lastSent.current = null;
    }, [savedTeam1, savedTeam2]);

    const submit = () => {
        if (!team1Ref.current || !team2Ref.current) return;
        const team1 = toScore(team1Ref.current.value);
        const team2 = toScore(team2Ref.current.value);
        team1Ref.current.value = team1;
        team2Ref.current.value = team2;
        const unchanged = team1 === savedTeam1 && team2 === savedTeam2;
        const alreadySent = lastSent.current === `${team1}-${team2}`;
        if (unchanged || alreadySent) return;
        lastSent.current = `${team1}-${team2}`;
        onSave(team1, team2);
    };

    const onKeyDown = (event) => {
        if (event.key === 'Enter') submit();
    };

    if (readOnly) {
        return (
            <div className="set-row">
                <span className="set-label">Set {number}</span>
                <span className={`ro-score team-a${savedTeam1 > savedTeam2 ? ' leading' : ''}`}>{savedTeam1}</span>
                <span className="score-dash" aria-hidden="true">
                    –
                </span>
                <span className={`ro-score team-b${savedTeam2 > savedTeam1 ? ' leading' : ''}`}>{savedTeam2}</span>
                {setItem.updatedAt && (
                    <span className="set-updated" title={formatExact(setItem.updatedAt)}>
                        Score updated {formatWhen(setItem.updatedAt, now)}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="set-row">
            <span className="set-label">Set {number}</span>
            <input
                className={`score-input team-a${savedTeam1 > savedTeam2 ? ' leading' : ''}`}
                type="number"
                min="0"
                inputMode="numeric"
                aria-label={`Set ${number} score, team A`}
                defaultValue={savedTeam1}
                ref={team1Ref}
                onKeyDown={onKeyDown}
                onBlur={submit}
            />
            <span className="score-dash" aria-hidden="true">
                –
            </span>
            <input
                className={`score-input team-b${savedTeam2 > savedTeam1 ? ' leading' : ''}`}
                type="number"
                min="0"
                inputMode="numeric"
                aria-label={`Set ${number} score, team B`}
                defaultValue={savedTeam2}
                ref={team2Ref}
                onKeyDown={onKeyDown}
                onBlur={submit}
            />
            <button
                className="btn btn-ghost btn-icon btn-sm btn-icon-danger"
                aria-label={`Delete set ${number}`}
                onClick={onDelete}
            >
                <TrashIcon size={16} />
            </button>
            {setItem.updatedAt && (
                <span className="set-updated" title={formatExact(setItem.updatedAt)}>
                    Score updated {formatWhen(setItem.updatedAt, now)}
                </span>
            )}
        </div>
    );
}

export default SetRow;
